/**
 * M4.V6 — the Workload view (`specs/M4-V6-workload.md`).
 *
 * One row per resource, one capacity cell per week or month bucket. The grid itself is pure
 * (`buildWorkloadGrid` in `packages/shared`), so the arithmetic that decides "who is
 * over-committed in week N" is unit-tested against the account's own five `settings_str` rows
 * and this file only paints it.
 *
 * It writes nothing but its own settings: monday's cells are read-only summaries and there is no
 * drag. Clicking a cell opens the item list for that resource and bucket (`[V6§6]`, `[V6§9 W10]`),
 * which is a **read** — round 10j's P-148, where the click used to `navigate()` to the route the
 * user was already on.
 *
 * Round 10j also closes the other two defects of this file: the toolbar's *Today* and the
 * resource row were dead controls with no `onClick` at all (P-143), and the settings pane's
 * *Resource ▸ Groups* option — offered since the pane existed — collapsed the whole board into a
 * single *Unassigned* row, because the resource axis only ever resolved a `people` column
 * (P-150).
 */
import {
  addDays,
  buildWorkloadGrid,
  createTimelineAxis,
  cellSpan,
  isBoardGroupToken,
  spanOfDays,
  workloadBuckets,
  workloadDateColumnFor,
  WORKLOAD_METRICS,
  WORKLOAD_PX_PER_DAY,
  WORKLOAD_UNASSIGNED,
  type WorkloadItemRef,
  type WorkloadViewSettings,
} from '@elizmonday/shared';
import { colors, PageEmptyState, PageSkeleton, useOverlayDismiss } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { BoardColumn, BoardDataSource, ItemRow } from '../../board-view/data/types';
import { PersonAvatar } from '../../board-view/columns/primitives';
import { useViewData } from '../shared/use-view-data';
import { BoardViewToolbar, useViewQueryControls } from '../shared/BoardViewToolbar';
import { ViewBarPane } from '../shared/ViewBarPane';
import { useViewSettings, type ViewSettingsOverride } from '../shared/use-view-settings';
import { ViewToolbar, type SettingsField } from '../shared/ViewToolbar';
import '../gantt/timeline.css';

/**
 * **P-143.** How much of the axis the *Today* button brings with it: one bucket of context on
 * each side of the bucket today falls in, so the button always has somewhere to scroll to and a
 * board whose work is all in the past still paints today when it is asked to.
 */
const BUCKET_CONTEXT_DAYS: Record<'week' | 'month', number> = { week: 7, month: 31 };

/** The separator between a resource row and one of its expanded item rows (P-143). */
const ITEM_ROW_SEPARATOR = '::';

export interface WorkloadViewProps {
  boardId: string;
  viewId: string;
  source: BoardDataSource;
  settingsOverride?: ViewSettingsOverride<WorkloadViewSettings>;
  today?: string;
}

const PAGE_CEILING = 400;

export function WorkloadView({ boardId, viewId, source, settingsOverride, today: frozenToday }: WorkloadViewProps) {
  const handle = useViewSettings<WorkloadViewSettings>(boardId, viewId, 'workload', settingsOverride);
  const settings = handle.settings;
  const navigate = useNavigate();
  const today = frozenToday ?? new Date().toISOString().slice(0, 10);

  /** P-025: the board toolbar, on the workload too (`supportsSort: false` hides its Sort). */
  const toolbar = useViewQueryControls();
  const data = useViewData({ source, boardId, viewId, state: toolbar.state });
  useEffect(() => {
    data.loadAll(PAGE_CEILING);
  }, [data]);

  const columns = data.payload?.columns ?? [];
  const people = data.payload?.people ?? [];
  const dateColumns = useMemo(
    () => columns.filter((column) => column.type === 'timeline' || column.type === 'date'),
    [columns],
  );
  const spanColumnId = useMemo(
    () => workloadDateColumnFor(settings, dateColumns),
    [settings, dateColumns],
  );
  /**
   * **P-150.** The resource axis is a people column **or the board's own groups** (`[V6§5]`), and
   * the picker has always offered both — while this resolved a `people` column and nothing else,
   * so `resourceBy: "groups"` left every row with no resource and the grid collapsed to one
   * *Unassigned* row holding the whole board.
   */
  const byGroups = settings.resourceBy !== null && isBoardGroupToken(settings.resourceBy);
  const resourceColumn = useMemo(
    () =>
      byGroups
        ? undefined
        : (columns.find((column) => column.id === settings.resourceBy && column.type === 'people') ??
          columns.find((column) => column.type === 'people')),
    [byGroups, columns, settings.resourceBy],
  );
  const effortColumn = useMemo(
    () => columns.find((column) => column.id === settings.effortColumnId),
    [columns, settings.effortColumnId],
  );

  /** The board's groups, in board order — the resource axis under *Resource ▸ Groups*. */
  const boardGroups = useMemo(
    () => (data.payload?.groups ?? []).filter((group) => !group.id.startsWith('gb:')),
    [data.payload],
  );

  const items = useMemo<WorkloadItemRef[]>(() => {
    const out: WorkloadItemRef[] = [];
    for (const group of data.payload?.groups ?? []) {
      for (const row of data.rowsOf(group.id)) {
        const span = spanColumnId === null ? null : cellSpan(row.values[spanColumnId]);
        out.push({
          id: row.id,
          name: row.name,
          // P-150: the row's board group *is* the resource under `resourceBy: "groups"`, and
          // `WORKLOAD_UNASSIGNED` then cannot happen — an item is always in exactly one group.
          resourceIds: byGroups ? [group.id] : resourceIdsOf(row, resourceColumn),
          span: span === null ? null : { from: span.start, to: span.end },
          effortValue: effortColumn === undefined ? null : numberOf(row.values[effortColumn.id]),
        });
      }
    }
    return out;
  }, [data, byGroups, spanColumnId, resourceColumn, effortColumn]);

  /**
   * **P-143.** *Today* had no `onClick` at all. It now scrolls the canvas to the bucket today
   * falls in — the three lines the gantt's own *Today* uses — and, because this axis is built
   * from the **data's** extent rather than from a fixed window, it also brings that bucket into
   * the range with one bucket of context on either side. Without the second half the button is
   * inert on exactly the board that needs it: one whose work is all in another quarter, where
   * today is not on the canvas to be scrolled to. q533 is open on what monday's own button does
   * beyond scrolling.
   */
  const [focusToday, setFocusToday] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const range = useMemo(() => {
    const days = items.flatMap((item) => (item.span === null ? [] : [item.span.from, item.span.to]));
    const context = BUCKET_CONTEXT_DAYS[settings.bucket];
    const focus = focusToday ? [addDays(today, -context), addDays(today, context)] : [];
    return spanOfDays([...days, ...focus].length > 0 ? [...days, ...focus] : [today]) ?? { from: today, to: today };
  }, [items, today, focusToday, settings.bucket]);

  const buckets = useMemo(
    () => workloadBuckets({ from: range.from, to: range.to, bucket: settings.bucket }),
    [range.from, range.to, settings.bucket],
  );

  /**
   * **P-143.** Which resource rows are expanded into the items behind them — the chevron's own
   * promise (`[V6§7]` puts a *collapse* chevron at panel x + 12 and nothing read it). It is
   * component state, not a setting: no captured `settings_str` key carries it and q533 has not
   * been answered, so nothing is written to the view row over a guess.
   */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const resourceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of items) for (const id of item.resourceIds) ids.add(id);
    // P-150: under *Groups* the axis is the board's own group order, not a people list.
    const ordered = byGroups
      ? boardGroups.filter((group) => ids.has(group.id)).map((group) => group.id)
      : people.filter((person) => ids.has(String(person.id))).map((person) => String(person.id));
    const rest = [...ids].filter((id) => !ordered.includes(id));
    const unassigned =
      !byGroups && items.some((item) => item.resourceIds.length === 0 && item.span !== null)
        ? [WORKLOAD_UNASSIGNED]
        : [];
    return [...ordered, ...rest, ...unassigned];
  }, [items, people, byGroups, boardGroups]);

  /**
   * **P-143.** An expanded resource's items are rows of the **same** axis: one sub-row per item,
   * directly under its resource, carrying that item's own load in each bucket. That keeps one
   * row model — the side panel, the lanes and `buildWorkloadGrid` all read this list — instead of
   * bolting a second kind of row onto the canvas.
   */
  const gridItems = useMemo<WorkloadItemRef[]>(
    () =>
      items.map((item) => {
        const sub = item.resourceIds
          .filter((resourceId) => expanded.has(resourceId))
          .map((resourceId) => `${resourceId}${ITEM_ROW_SEPARATOR}${item.id}`);
        return sub.length === 0 ? item : { ...item, resourceIds: [...item.resourceIds, ...sub] };
      }),
    [items, expanded],
  );

  /** The axis in paint order: each resource, then its items while it is expanded. */
  const rowIds = useMemo(() => {
    const out: string[] = [];
    for (const resourceId of resourceIds) {
      out.push(resourceId);
      if (!expanded.has(resourceId)) continue;
      for (const item of items) {
        if (item.resourceIds.includes(resourceId)) {
          out.push(`${resourceId}${ITEM_ROW_SEPARATOR}${item.id}`);
        }
      }
    }
    return out;
  }, [resourceIds, expanded, items]);

  const grid = useMemo(
    () => buildWorkloadGrid({ settings, items: gridItems, buckets, resourceIds: rowIds }),
    [settings, gridItems, buckets, rowIds],
  );

  /** The same axis the Gantt draws, with the bucket as its minor unit. */
  const axis = useMemo(
    () => createTimelineAxis({
      zoom: settings.bucket === 'week' ? 'weeks' : 'months',
      from: range.from,
      to: range.to,
      pxPerDay: WORKLOAD_PX_PER_DAY,
    }),
    [settings.bucket, range.from, range.to],
  );

  const fields = useWorkloadFields(settings, handle.save, columns);

  /** A row id back into its parts: `<resourceId>` or `<resourceId>::<itemId>`. */
  const rowParts = useCallback((rowId: string) => {
    const at = rowId.indexOf(ITEM_ROW_SEPARATOR);
    return at < 0
      ? { resourceId: rowId, itemId: null as string | null }
      : { resourceId: rowId.slice(0, at), itemId: rowId.slice(at + ITEM_ROW_SEPARATOR.length) };
  }, []);

  const rowLabel = useCallback(
    (rowId: string): string => {
      const { resourceId, itemId } = rowParts(rowId);
      if (itemId !== null) return items.find((item) => item.id === itemId)?.name ?? itemId;
      if (resourceId === WORKLOAD_UNASSIGNED) return 'Unassigned';
      if (byGroups) return boardGroups.find((group) => group.id === resourceId)?.title ?? resourceId;
      return people.find((person) => String(person.id) === resourceId)?.name ?? resourceId;
    },
    [rowParts, items, byGroups, boardGroups, people],
  );

  /**
   * **P-148.** `[V6§9 W10]`: "A cell click opens the item list filtered to that resource and
   * bucket." It used to `navigate('/boards/:boardId/views/:viewId')` — the route the user was
   * already on — so the click was indistinguishable from a miss. The cell is a **read** (`[V6§6]`
   * — the workload writes nothing but its own settings), so it opens the list in `ViewBarPane`,
   * the same pane the rest of this bar lists things in, and a row of it opens the item card.
   */
  const [listFor, setListFor] = useState<{ rowId: string; bucketKey: string; anchor: HTMLElement } | null>(
    null,
  );

  const listedItems = useMemo(() => {
    if (listFor === null) return [];
    const { resourceId, itemId } = rowParts(listFor.rowId);
    const bucket = buckets.find((entry) => entry.key === listFor.bucketKey);
    if (bucket === undefined) return [];
    return items.filter((item) => {
      if (itemId !== null) return item.id === itemId;
      if (!item.resourceIds.includes(resourceId)) return false;
      // The bucket's own rule: the span has to overlap the bucket's days (`[V6§2.3]`).
      return item.span !== null && item.span.from <= bucket.to && item.span.to >= bucket.from;
    });
  }, [listFor, rowParts, buckets, items]);

  /* P-013's shared contract for a surface that renders itself: Escape closes it. */
  useOverlayDismiss({ open: listFor !== null, onClose: () => setListFor(null) });

  if (!handle.ready || (data.pending && data.payload === null)) return <PageSkeleton variant="board" />;
  if (data.error !== null) return <PageEmptyState title="This view could not be loaded" hint={data.error.message} />;

  const bucketBox = (key: string) => {
    const bucket = buckets.find((entry) => entry.key === key);
    if (bucket === undefined) return { x: 0, width: 0 };
    return axis.bar(bucket.from, bucket.to);
  };

  return (
    <div
      className="ez-tl"
      data-testid="workload-view"
      data-bucket={settings.bucket}
      style={{ ['--ez-tl-side' as string]: `${WORKLOAD_METRICS.sidePanelWidth}px` }}
    >
      <BoardViewToolbar
        controls={toolbar.controls}
        boardId={boardId}
        viewId={viewId}
        registryId="workload"
        source={source}
        payload={data.payload}
        onChanged={() => data.refresh()}
        gear={<ViewToolbar title="Workload" fields={fields} testId="workload-settings" />}
        extras={
      <div className="ez-tl__toolbar" data-testid="workload-toolbar">
        {/* P-143: bring today's bucket onto the canvas, then scroll to it. */}
        <button
          type="button"
          className="ez-tl__button"
          data-testid="workload-today"
          onClick={() => {
            setFocusToday(true);
            // After the range has repainted: the same three lines the gantt's Today uses.
            requestAnimationFrame(() => {
              const element = bodyRef.current;
              if (element === null) return;
              const box = axis.bar(today, today);
              element.scrollLeft = Math.max(0, box.x - WORKLOAD_METRICS.sidePanelWidth / 2);
            });
          }}
        >
          Today
        </button>
        <div className="ez-tl__zoom" data-testid="workload-bucket">
          <select
            aria-label="Bucket"
            value={settings.bucket}
            onChange={(event) => handle.save({ bucket: event.target.value as 'week' | 'month' })}
          >
            <option value="week">Weeks</option>
            <option value="month">Months</option>
          </select>
        </div>
      </div>
        }
      />

      <div className="ez-tl__body" ref={bodyRef} data-testid="workload-body">
        <div className="ez-tl__side" style={{ width: WORKLOAD_METRICS.sidePanelWidth }} data-testid="workload-side-panel">
          <div className="ez-tl__header">
            <div className="ez-tl__header-row ez-tl__header-row--major" />
            <div className="ez-tl__header-row ez-tl__header-row--minor" />
          </div>
          {grid.resourceIds.map((rowId) => {
            const { resourceId, itemId } = rowParts(rowId);
            const person = byGroups ? undefined : people.find((entry) => String(entry.id) === resourceId);
            const group = byGroups ? boardGroups.find((entry) => entry.id === resourceId) : undefined;
            const name = rowLabel(rowId);
            const open = expanded.has(rowId);
            return (
              <button
                key={rowId}
                type="button"
                className="ez-wl__resource"
                data-testid="workload-resource"
                data-resource={rowId}
                /* P-143: a resource row and one of its expanded item rows are both rows of this
                   axis; `data-kind` is what tells them apart. */
                data-kind={itemId === null ? 'resource' : 'item'}
                data-expanded={itemId === null ? String(open) : undefined}
                aria-expanded={itemId === null ? open : undefined}
                onClick={() => {
                  if (itemId !== null) {
                    navigate(`/boards/${boardId}/views/${viewId}/pulses/${itemId}`);
                    return;
                  }
                  setExpanded((current) => {
                    const next = new Set(current);
                    if (!next.delete(rowId)) next.add(rowId);
                    return next;
                  });
                }}
              >
                {itemId === null ? <span className="ez-wl__chevron" data-open={String(open)}>›</span> : null}
                {itemId !== null ? (
                  <span className="ez-wl__item-name">{name}</span>
                ) : byGroups ? (
                  /* P-150: a group row wears its own colour dot and title, not an avatar. */
                  <>
                    <span className="ez-wl__group-dot" style={{ background: group?.color ?? colors.emptyStatus }} />
                    <span className="ez-wl__name">{name}</span>
                  </>
                ) : (
                  <>
                    {/* B4's own avatar, so a monday colour **name** resolves to its hex. */}
                    <span className="ez-wl__avatar">
                      <PersonAvatar name={name} color={person?.color ?? 'explosive'} photoUrl={person?.photoUrl} size={30} />
                    </span>
                    <span className="ez-wl__name">{name}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>

        <div className="ez-tl__canvas" style={{ width: axis.width }} data-testid="workload-canvas">
          <div className="ez-tl__header">
            <div className="ez-tl__header-row ez-tl__header-row--major">
              {axis.major().map((cell) => (
                <div key={cell.key} className="ez-tl__cell ez-tl__cell--major" style={{ left: cell.x, width: cell.width }}>
                  <span className="ez-tl__cell-label">{cell.label}</span>
                </div>
              ))}
            </div>
            <div className="ez-tl__header-row ez-tl__header-row--minor">
              {axis.minor().map((cell) => (
                <div key={cell.key} className="ez-tl__cell ez-tl__cell--minor" style={{ left: cell.x, width: cell.width }}>
                  {cell.label}
                </div>
              ))}
            </div>
          </div>

          <div className="ez-tl__rows" style={{ height: grid.resourceIds.length * WORKLOAD_METRICS.resourceRowHeight }}>
            {grid.resourceIds.map((rowId, index) => (
              <div
                key={rowId}
                className="ez-wl__lane"
                style={{ position: 'absolute', top: index * WORKLOAD_METRICS.resourceRowHeight, left: 0, right: 0 }}
              >
                {buckets.map((bucket) => {
                  const cell = grid.cell(rowId, bucket.key);
                  if (cell === undefined) return null;
                  const box = bucketBox(bucket.key);
                  return (
                    <button
                      key={bucket.key}
                      type="button"
                      className="ez-wl__cell"
                      style={{ left: box.x + 2, width: Math.max(0, box.width - 4) }}
                      data-testid="workload-cell"
                      data-resource={rowId}
                      data-bucket={bucket.key}
                      data-load={String(cell.load)}
                      data-capacity={cell.capacity === null ? '' : String(cell.capacity)}
                      data-over={cell.over ? 'true' : 'false'}
                      disabled={cell.load === 0}
                      // P-148: the cell is a read — it opens the list behind the number.
                      onClick={(event) =>
                        setListFor({ rowId, bucketKey: bucket.key, anchor: event.currentTarget })
                      }
                    >
                      {cell.load === 0 ? '' : cell.capacity === null ? String(cell.load) : `${cell.load}/${cell.capacity}`}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {listFor === null ? null : (
        /**
         * **P-148.** B6's filtered item list for one cell: the items of that resource whose span
         * overlaps that bucket, in the pane the rest of this bar uses (`ViewBarPane` — a body
         * portal, placed against the cell, and a bottom sheet on a phone).
         */
        <ViewBarPane
          anchor={listFor.anchor}
          label={`${rowLabel(listFor.rowId)} · ${listFor.bucketKey}`}
          testId="workload-item-list"
          onClose={() => setListFor(null)}
        >
          <div className="ez-wl__list-head" data-testid="workload-item-list-head">
            {`${rowLabel(listFor.rowId)} · ${listFor.bucketKey} · ${String(listedItems.length)} items`}
          </div>
          {listedItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ez-view-bar__row"
              data-testid={`workload-item-${item.id}`}
              onClick={() => {
                setListFor(null);
                navigate(`/boards/${boardId}/views/${viewId}/pulses/${item.id}`);
              }}
            >
              {item.name}
            </button>
          ))}
        </ViewBarPane>
      )}
    </div>
  );
}

/** The people a row loads. An empty cell means the *Unassigned* row (`[V6§9 W9]`). */
function resourceIdsOf(row: ItemRow, column: BoardColumn | undefined): string[] {
  if (column === undefined) return [];
  const value = row.values[column.id];
  if (value === null || value === undefined || typeof value !== 'object') return [];
  const list = (value as { personsAndTeams?: { id: number | string }[] }).personsAndTeams;
  if (!Array.isArray(list)) return [];
  return list.map((entry) => String(entry.id));
}

function numberOf(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function useWorkloadFields(
  settings: WorkloadViewSettings,
  save: (patch: Partial<WorkloadViewSettings>) => void,
  columns: readonly { id: string; title: string; type: string }[],
): SettingsField[] {
  return useMemo(() => {
    const peopleColumns = columns.filter((column) => column.type === 'people');
    const numberColumns = columns.filter((column) => column.type === 'numbers');
    const dateColumns = columns
      .filter((column) => column.type === 'timeline' || column.type === 'date')
      .map((column) => ({ id: column.id, title: column.title, type: column.type }));
    return [
      {
        kind: 'select',
        id: 'group_by_id',
        label: 'Resource',
        // P-150: *Groups* is a mode the view implements now, not one the picker only offers.
        value: settings.resourceBy ?? (peopleColumns[0]?.id ?? 'groups'),
        options: [
          { value: 'groups', label: 'Groups' },
          ...peopleColumns.map((column) => ({ value: column.id, label: column.title })),
        ],
        onChange: (value) => save({ resourceBy: value }),
      },
      {
        kind: 'columns',
        id: 'columns',
        label: 'Time columns',
        columns: dateColumns,
        selection: settings.dateColumns,
        // P-146: `workloadDateColumnFor` falls back to the whole pool when nothing is chosen.
        emptyMeans: 'all',
        onChange: (dateColumnsNext) => save({ dateColumns: dateColumnsNext }),
      },
      {
        kind: 'radio',
        id: 'effort_column_type',
        label: 'Effort',
        value: settings.effortMode,
        options: [
          { value: 'default-label-count', label: 'Count items' },
          { value: 'effort_column', label: 'Effort column' },
        ],
        onChange: (value) => save({ effortMode: value as WorkloadViewSettings['effortMode'] }),
      },
      {
        kind: 'select',
        id: 'effortColumnIdsByBoardId',
        label: 'Effort column',
        value: settings.effortColumnId ?? '',
        options: [{ value: '', label: 'None' }, ...numberColumns.map((column) => ({ value: column.id, label: column.title }))],
        onChange: (value) => save({ effortColumnId: value === '' ? null : value }),
      },
      {
        kind: 'select',
        id: 'default_capacity',
        label: 'Default capacity',
        value: settings.defaultCapacity,
        // monday stores a string; `''` is "no capacity", not zero (`[V6§2.4]`).
        options: ['', '1', '2', '3', '4', '5', '8', '10', '20', '40'].map((value) => ({
          value,
          label: value === '' ? 'No capacity' : value,
        })),
        onChange: (value) => save({ defaultCapacity: value }),
      },
    ];
  }, [settings, save, columns]);
}
